'use client'

import React, { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { motion, AnimatePresence, Variants } from 'framer-motion';

import { Button } from './Button';
import { Icon } from './Icon';
import { trackEvent } from '@/lib/analytics';

interface WebformElement {
  '#title'?: string;
  '#type'?: string;
  '#required'?: boolean;
  '#attributes'?: Record<string, string>;
  '#default_value'?: string;
  '#access'?: boolean;
  '#empty_option'?: string;
  '#options'?: Record<string, string>;
  [key: string]: unknown;
}

type WebformElements = Record<string, WebformElement>;

interface FormProps {
  formId: string;
  submitLabel: string;
  theme?: 'light' | 'dark';
  onSuccess?: (response: Record<string, unknown>) => void;
  lang?: string;
}

const FIELD_TRANSLATIONS: Record<string, Record<string, { title?: string; placeholder?: string }>> = {
  es: {
    name:    { title: 'Nombre',             placeholder: 'Escribe tu nombre' },
    email:   { title: 'Correo electrónico', placeholder: 'Escribe tu correo' },
    company: { title: 'Empresa',            placeholder: 'Escribe tu empresa' },
    phone:   { title: 'Teléfono',           placeholder: 'Escribe tu teléfono' },
    country: { title: 'País',               placeholder: 'Escribe tu país' },
    message: { title: 'Mensaje',            placeholder: 'Escribe tu mensaje' },
    opt_in:  { title: 'Sí, me gustaría recibir comunicaciones de Agathos Technologies.' },
  },
};

function applyFieldTranslations(elements: WebformElements, lang: string): WebformElements {
  const overrides = FIELD_TRANSLATIONS[lang];
  if (!overrides) return elements;

  const translate = (key: string, config: WebformElement): WebformElement => {
    const o = overrides[key];
    if (!o) return config;
    const updated = { ...config };
    if (o.title) updated['#title'] = o.title;
    if (o.placeholder && config['#attributes']) {
      updated['#attributes'] = { ...config['#attributes'] as Record<string, string>, placeholder: o.placeholder };
    }
    return updated;
  };

  return Object.fromEntries(
    Object.entries(elements).map(([key, config]) => {
      if (!config || typeof config !== 'object') return [key, config];
      if (config['#type'] === 'webform_flexbox' || config['#type'] === 'flexbox' || key.startsWith('row_')) {
        return [key, applyFieldTranslations(config as WebformElements, lang)];
      }
      return [key, translate(key, config)];
    })
  );
}

const formCascade: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease: "easeOut"
    }
  })
};

export default function Form({ formId, submitLabel, theme = 'dark', onSuccess, lang = 'en' }: FormProps) {
  const [elements, setElements] = useState<WebformElements | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const hasTrackedStart = useRef(false);
  const methods = useForm();
  const { formState: { errors } } = methods;

  const focusRing = theme === 'dark'
    ? 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3CABE2]'
    : 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary';

  const styles = {
    label: theme === 'dark' ? 'text-white/70' : 'text-black/70',
    input: theme === 'dark'
      ? `bg-white/5 border-white/10 text-white focus:border-[#3CABE2] focus:bg-white/10 shadow-[inset_0_1px_2px_rgba(255,255,255,0.05)] ${focusRing}`
      : `bg-black/5 border-black/10 text-black focus:border-[#0A4492] focus:bg-white ${focusRing}`,
    checkbox: theme === 'dark' ? `accent-[#3CABE2] border-white/20 ${focusRing}` : `accent-[#0A4492] border-black/20 ${focusRing}`,
    selectIcon: theme === 'dark' ? 'invert' : '',
  };

  useEffect(() => {
    async function getForm(lang: string = 'en') {
      try {
        const res = await fetch(`/api/form/elements?formId=${formId}&lang=${lang}`);

        if (!res.ok) throw new Error(`Form fetch failed: ${res.status}`);

        const data: WebformElements = await res.json();
        setElements(data);
      }
      catch (err) {
        console.error("Error fetching form schema:", err);
      }
    }
    getForm(lang);
  }, [lang, formId]);

  const handleDrupalSubmit = async (values: FieldValues) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const payload = {
      webform_id: formId,
      ...values,
    };

    try {
      const res = await fetch('/api/form/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const result = await res.json();
        setSubmitStatus('success');
        trackEvent('form_submit', { form_id: formId });
        methods.reset();
        if (onSuccess) onSuccess(result);
      }
      else {
        setSubmitStatus('error');
        trackEvent('form_error', { form_id: formId, error_type: 'server_error' });
      }
    }
    catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus('error');
      trackEvent('form_error', { form_id: formId, error_type: 'network_error' });
    }
    finally {
      setIsSubmitting(false);
    }
  };

  if (!elements) return (
    <div role="status" aria-live="polite" className="animate-pulse py-10 opacity-20 text-white">
      {lang === 'es' ? 'Cargando...' : 'Loading...'}
    </div>
  );

  const renderField = (key: string, config: WebformElement) => {
    if (config['#type'] === 'hidden' || config['#access'] === false) {
      return (
        <input type="hidden" {...methods.register(key)} value={config['#default_value'] || ''} />
      );
    }

    const fieldError = errors[key];
    const errorId = `${key}-error`;
    const commonProps = {
      ...methods.register(key, { required: config['#required'] }),
      id: key,
      disabled: isSubmitting,
      'aria-required': config['#required'] || undefined,
      'aria-invalid': fieldError ? true as const : undefined,
      'aria-describedby': fieldError ? errorId : undefined,
    };

    const errorMessage = fieldError && (
      <p id={errorId} role="alert" className="text-red-400 text-xs mt-1">
        {config['#title']} {lang === 'es' ? 'es requerido' : 'is required'}
      </p>
    );

    switch (config['#type']) {
      case 'checkbox':
        return (
          <>
            <div className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                {...commonProps}
                className={`mt-1 lg:mt-0 w-5 h-5 rounded cursor-pointer ${styles.checkbox}`}
              />
              <label htmlFor={key} className={`text-xs lg:text-sm font-medium leading-tight ${styles.label}`}>
                {config['#title']} {config['#required'] && <span className="text-[#3CABE2]" aria-hidden="true">*</span>}
              </label>
            </div>
            {errorMessage}
          </>
        );

      case 'textarea':
        return (
          <>
            <textarea
              {...commonProps}
              placeholder={config['#attributes']?.placeholder}
              rows={4}
              className={`w-full rounded-md p-4 min-h-[120px] outline-none border transition-all resize-none ${styles.input}`}
            />
            {errorMessage}
          </>
        );

      case 'select':
        return (
          <>
            <div className="relative">
              <select
                {...commonProps}
                className={`w-full h-[54px] appearance-none border rounded-md p-4 outline-none transition-all cursor-pointer ${styles.input}`}
              >
                <option value="">{config['#empty_option'] || 'Select...'}</option>
                {config['#options'] && Object.entries(config['#options']).map(([val, label]) => (
                  <option key={val} value={val} className="bg-[#020713]">{label}</option>
                ))}
              </select>
              <div className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${styles.selectIcon}`} aria-hidden="true">
                <Icon name="ArrowDown" size={12} />
              </div>
            </div>
            {errorMessage}
          </>
        );

      default:
        return (
          <>
            <input
              {...commonProps}
              type={config['#type'] === 'email' ? 'email' : (config['#type'] === 'tel' ? 'tel' : 'text')}
              placeholder={config['#attributes']?.placeholder || ''}
              className={`w-full h-[54px] border rounded-md p-4 outline-none transition-all placeholder:text-white/30 ${styles.input}`}
            />
            {errorMessage}
          </>
        );
    }
  };

  const renderElements = (items: WebformElements) => {
    let fieldIndex = 0;

    return Object.entries(items).map(([key, config]) => {
      if (key.startsWith('#') || key === 'actions') return null;

      if (config['#type'] === 'flexbox' || config['#type'] === 'container' || key.startsWith('row_')) {
        return (
          <div key={key} className="flex flex-col md:flex-row gap-6 w-full mb-6 items-end">
            {renderElements(config as WebformElements)}
          </div>
        );
      }

      // Hide the wrapper div for hidden fields so it doesn't add spacing
      if (config['#type'] === 'hidden') return renderField(key, config);

      fieldIndex++;

      return (
        <motion.div 
          key={key} 
          custom={fieldIndex}
          initial="hidden"
          animate="visible"
          variants={formCascade}
          className="flex flex-col gap-2 w-full mb-6"
        >
          {config['#type'] !== 'checkbox' && (
            <label htmlFor={key} className={`text-[10px] uppercase tracking-[0.2em] font-bold ${styles.label}`}>
              {config['#title']} {config['#required'] && <span className="text-[#3CABE2]" aria-hidden="true">*</span>}
            </label>
          )}
          {renderField(key, config)}
        </motion.div>
      );
    });
  };

  return (
    <FormProvider {...methods}>
      <AnimatePresence mode="wait">
        {submitStatus === 'success' ? (
          <motion.div 
            key="success"
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-10 text-center lg:text-left"
          >
            <h3 className="text-[#3CABE2] text-2xl font-bold mb-4 uppercase tracking-tight">
              {lang === 'es' ? 'Mensaje Enviado' : 'Message Sent'}
            </h3>
            <p className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>
              {lang === 'es'
                ? 'Gracias. Nos pondremos en contacto contigo lo antes posible.'
                : "Thank you. We'll get back to you as soon as possible."}
            </p>
            <button
              onClick={() => setSubmitStatus('idle')}
              className="mt-6 text-xs uppercase tracking-widest font-bold border-b border-[#3CABE2] pb-1 text-[#3CABE2]"
            >
              {lang === 'es' ? 'Enviar otro mensaje' : 'Send another message'}
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            onSubmit={methods.handleSubmit(handleDrupalSubmit)}
            noValidate
            className="space-y-6"
            aria-label="Contact form"
            onFocus={() => {
              if (!hasTrackedStart.current) {
                hasTrackedStart.current = true;
                trackEvent('form_start', { form_id: formId });
              }
            }}
          >
            {renderElements(applyFieldTranslations(elements, lang))}

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.5 } }}
              className="pt-6 flex flex-col lg:flex-row items-center gap-6 text-center lg:text-left"
            >
              <Button
                type="submit"
                label={isSubmitting ? (lang === 'es' ? "ENVIANDO..." : "SENDING...") : submitLabel}
                iconRight={isSubmitting ? undefined : "ArrowUpRight"}
                disabled={isSubmitting}
                variant={theme === 'dark' ? 'outline' : 'primary'}
              />

              {submitStatus === 'error' && (
                <p role="alert" aria-live="assertive" className="text-red-400 text-xs font-bold uppercase tracking-widest">
                  {lang === 'es'
                    ? 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.'
                    : 'Error sending message. Please try again.'}
                </p>
              )}
            </motion.div>
          </motion.form>
        )}
      </AnimatePresence>
    </FormProvider>
  );
}
